import { useEditorMaybe } from '@grapesjs/react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  Typography,
} from '@mui/material';
import { RiCheckLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { appLinks } from 'src/lib/routing';
import convertToTitleCase from 'src/utils/convertToTitleCase';

import { type TagLayout, useTagLayoutLoadPage } from './useTagLayoutLoadPage';

const SEARCH_DEBOUNCE_MS = 300;

interface PalletTagStorageLoadModalProps {
  open: boolean;
  onClose: () => void;
  setTemplateName: (name: string) => void;
}

function isProjectData(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export default function PalletTagStorageLoadModal({ open, onClose, setTemplateName }: PalletTagStorageLoadModalProps) {
  const editor = useEditorMaybe();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setTerm(searchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const { layouts, hasNextPage, fetchMore } = useTagLayoutLoadPage(term);
  const [selectedTemplate, setSelectedTemplate] = useState<TagLayout | null>(null);

  const handleClose = () => {
    setSelectedTemplate(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogContent>
        <DialogContentText>Select document to open:</DialogContentText>

        <TextField
          size="small"
          fullWidth
          placeholder="Search layouts"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ mt: 1 }}
        />

        <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {layouts.map((template) => {
            const isSelected = selectedTemplate?.id === template.id;

            return (
              <Card key={template.name}>
                <CardActionArea
                  onClick={() => {
                    setSelectedTemplate(template);
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 'semibold' }}>{template.name}</Typography>
                      {isSelected && <RiCheckLine size={20} className="text-success-main" />}
                    </Box>
                    <Chip label={convertToTitleCase(template.tagType)} size="small" />
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
        {hasNextPage && (
          <AgButton variant="ghost" onPress={() => void fetchMore()}>
            Show more
          </AgButton>
        )}
      </DialogContent>
      <DialogActions>
        <AgButton variant="ghost" onPress={handleClose}>
          Cancel
        </AgButton>
        <AgButton
          variant="primary"
          isDisabled={!selectedTemplate}
          onPress={() => {
            if (!selectedTemplate || !editor) {
              return;
            }

            const template: unknown =
              typeof selectedTemplate.templateState === 'string'
                ? JSON.parse(selectedTemplate.templateState)
                : selectedTemplate.templateState;

            if (!isProjectData(template)) {
              return;
            }

            editor.loadProjectData(template);

            setTemplateName(selectedTemplate.name);

            void navigate(appLinks.palletTagEditor({ name: selectedTemplate.name }, { replace: true }));

            setSelectedTemplate(null);
            onClose();
          }}
        >
          Open
        </AgButton>
      </DialogActions>
    </Dialog>
  );
}
